/**
 * The upload manager.
 *
 * One Uppy instance lives at the root of the app for the whole session, which
 * is the single biggest thing the SPA buys: a transfer now survives navigating
 * between screens, opening a dialog, searching and paging, because none of
 * that unmounts anything it depends on.
 *
 * A full page reload still tears down the JS context - but under tus that is
 * no longer fatal either. Golden Retriever writes the queue to local storage
 * and tus resumes each file from its recorded offset, so a refresh mid-upload
 * picks up roughly where it left off instead of starting again.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import Uppy from '@uppy/core';
import Tus from '@uppy/tus';
import GoldenRetriever from '@uppy/golden-retriever';
import { toast } from 'sonner';

interface UploadTarget {
  /** Existing folder to upload into, or null for the owner's root. */
  folderId: string | null;
  description: string;
}

interface UploadContextValue {
  uppy: Uppy;
  /** Adds browser File objects, carrying their directory structure. */
  addFiles: (files: File[], target: UploadTarget) => void;
  cancelAll: () => void;
  state: UploadState;
}

export interface UploadState {
  active: boolean;
  total: number;
  complete: number;
  failed: number;
  bytesUploaded: number;
  bytesTotal: number;
  currentName: string | null;
}

const UploadContext = createContext<UploadContextValue | null>(null);

const EMPTY: UploadState = {
  active: false,
  total: 0,
  complete: 0,
  failed: 0,
  bytesUploaded: 0,
  bytesTotal: 0,
  currentName: null,
};

export function UploadProvider({
  children,
  onUploaded,
}: {
  children: ReactNode;
  /** Called once a batch settles, so the listing can refresh. */
  onUploaded: () => void;
}) {
  const [state, setState] = useState<UploadState>(EMPTY);
  // Held in a ref so the Uppy event handlers, which are registered once, always
  // call the current version rather than the one captured at mount.
  const onUploadedRef = useRef(onUploaded);
  onUploadedRef.current = onUploaded;

  const uppy = useMemo(() => {
    const instance = new Uppy({
      autoProceed: true,
      // Matches MAX_UPLOAD_BYTES on the server. Enforced there too - this is
      // just so a mistake is caught before the bytes are sent.
      restrictions: { maxFileSize: 5 * 1024 * 1024 * 1024 },
    })
      .use(Tus, {
        endpoint: '/api/uploads',
        // Bounded PATCH requests rather than one enormous body. This is what
        // lets the server keep its request timeout and Caddy keep a sane
        // request_body limit.
        chunkSize: 50 * 1024 * 1024,
        // The session is a cookie, and the endpoint is same-origin.
        withCredentials: true,
        retryDelays: [0, 1000, 3000, 5000, 10000],
        limit: 3,
      })
      .use(GoldenRetriever, { serviceWorker: false });

    return instance;
  }, []);

  useEffect(() => {
    const recompute = () => {
      const files = uppy.getFiles();
      const complete = files.filter((f) => f.progress?.uploadComplete).length;
      const failed = files.filter((f) => f.error).length;
      const inFlight = files.find((f) => f.progress?.uploadStarted && !f.progress.uploadComplete);

      setState({
        active: files.length > 0 && complete + failed < files.length,
        total: files.length,
        complete,
        failed,
        bytesUploaded: files.reduce((sum, f) => sum + Number(f.progress?.bytesUploaded ?? 0), 0),
        bytesTotal: files.reduce((sum, f) => sum + Number(f.progress?.bytesTotal ?? f.size ?? 0), 0),
        currentName: inFlight?.name ?? null,
      });
    };

    const onError = (file: { name?: string } | undefined, error: { message: string }) => {
      toast.error(`${file?.name ?? 'Upload'} failed`, { description: error.message });
      recompute();
    };

    const onComplete = (result: { successful?: unknown[]; failed?: unknown[] }) => {
      recompute();
      const ok = result.successful?.length ?? 0;
      const bad = result.failed?.length ?? 0;
      if (ok > 0) {
        toast.success(`${ok} file${ok === 1 ? '' : 's'} uploaded`);
        onUploadedRef.current();
      }
      if (bad > 0) toast.error(`${bad} file${bad === 1 ? '' : 's'} failed`);
      // Clear settled files so a later batch starts from a clean slate rather
      // than re-reporting a count that includes everything from this session.
      if (bad === 0) {
        setTimeout(() => {
          for (const file of uppy.getFiles()) {
            if (file.progress?.uploadComplete) uppy.removeFile(file.id);
          }
          setState(EMPTY);
        }, 2500);
      }
    };

    uppy.on('upload-progress', recompute);
    uppy.on('upload-success', recompute);
    uppy.on('file-added', recompute);
    uppy.on('file-removed', recompute);
    uppy.on('upload-error', onError);
    uppy.on('complete', onComplete);

    return () => {
      uppy.off('upload-progress', recompute);
      uppy.off('upload-success', recompute);
      uppy.off('file-added', recompute);
      uppy.off('file-removed', recompute);
      uppy.off('upload-error', onError);
      uppy.off('complete', onComplete);
    };
  }, [uppy]);

  /**
   * A refresh during an upload is survivable now, but it is still not what
   * anyone means to do, so it is worth one confirmation. The wording is the
   * browser's own; custom text has been ignored for years.
   */
  useEffect(() => {
    if (!state.active) return;
    const guard = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, [state.active]);

  const value = useMemo<UploadContextValue>(
    () => ({
      uppy,
      state,
      addFiles: (files, target) => {
        for (const file of files) {
          // webkitRelativePath is set for a directory pick; Uppy sets
          // relativePath itself for a drag-dropped folder. Either way the
          // server reads the directory part and rebuilds the tree.
          const relativePath =
            (file as File & { webkitRelativePath?: string }).webkitRelativePath || null;
          try {
            uppy.addFile({
              name: file.name,
              type: file.type,
              data: file,
              meta: {
                folderId: target.folderId,
                description: target.description,
                ...(relativePath ? { relativePath } : {}),
              },
            });
          } catch (error) {
            toast.error(`Could not add ${file.name}`, {
              description: (error as Error).message,
            });
          }
        }
      },
      cancelAll: () => {
        uppy.cancelAll();
        setState(EMPTY);
      },
    }),
    [uppy, state],
  );

  return <UploadContext.Provider value={value}>{children}</UploadContext.Provider>;
}

export function useUploads(): UploadContextValue {
  const context = useContext(UploadContext);
  if (!context) throw new Error('useUploads must be used inside UploadProvider');
  return context;
}
